import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { registrationService } from '../../services/registrationService';
import { favoriteService } from '../../services/favoriteService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import LoadingSpinner from '../../components/LoadingSpinner';
import ProfileHeader from './components/ProfileHeader';
import ActivityTimeline from './components/ActivityTimeline';
import DigitalTickets from './components/DigitalTickets';
import MembershipPlan from './components/MembershipPlan';
import UserListings from './components/UserListings';

import AchievementShowcase from './components/AchievementShowcase';
import EditProfileModal from './components/EditProfileModal';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import EventCard from '../event-discovery/components/EventCard';
import SpotCard from '../spot-guide/components/SpotCard';
import ProductCard from '../official-store/components/ProductCard';
import ListingCard from '../user-marketplace/components/ListingCard';
import UpgradeModal from './components/UpgradeModal';
import { membershipService } from '../../services/membershipService';
import { getUserOrders } from '../../services/orderService';
import OrderHistory from './components/OrderHistory';
import SettingsTab from './components/SettingsTab';


const UserProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, profileLoading, updateProfile } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [activities, setActivities] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  // Add this helper function
  const isFieldMissing = (value, type = 'default') => {
    if (type === 'tc') {
      return !value || value?.length !== 11;
    }
    return !value || value?.trim() === '';
  };

  const user = userProfile ? {
    name: userProfile?.full_name || 'User',
    username: userProfile?.email?.split('@')?.[0] || 'user',
    avatar: userProfile?.avatar_url || '',
    avatarAlt: `Profile picture of ${userProfile?.full_name || 'User'}`,
    bio: userProfile?.bio || '',
    memberSince: new Date(userProfile.created_at)?.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long' }),
    experienceLevel: userProfile?.experience_level || '',
    hasEquipment: userProfile?.has_equipment || '',
    email: userProfile?.email || '',
    phone: userProfile?.phone || '',
    // TC number and emergency contact fields
    tcNumber: userProfile?.tc_number || '',
    birthDate: userProfile?.birth_date || '',
    emergencyContactName: userProfile?.emergency_contact_name || '',
    emergencyContactPhone: userProfile?.emergency_contact_phone || '',
    // Shipping address fields (snake_case to camelCase conversion)
    city: userProfile?.city || userProfile?.location || '',
    district: userProfile?.district || '',
    addressLine: userProfile?.address_line || '',
    zipCode: userProfile?.zip_code || '',
    // Location from registration
    location: userProfile?.location || '',
    // Social media & equipment fields (snake_case to camelCase conversion)
    instagramHandle: userProfile?.instagram_handle || '',
    websiteUrl: userProfile?.website_url || '',
    equipmentDetails: userProfile?.equipment_details || '',
    // SUP Information fields (snake_case to camelCase conversion)
    weightKg: userProfile?.weight_kg || '',
    heightCm: userProfile?.height_cm || '',
    isPro: userProfile?.membership_tier === 'pro',
    badges: [],
    stats: {
      eventsAttended: 0,
      spotsVisited: 0,
      reviewsGiven: 0
    }
  } : null;

  // NEW: Handle hash navigation to open specific sections
  useEffect(() => {
    const hash = location?.hash?.replace('#', '');
    if (hash === 'ilanlarim') {
      setActiveSection('listings');
      // Scroll to top after setting section
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (hash === 'orders') {
      setActiveSection('orders');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  const sections = [
  { id: 'overview', label: 'Genel Bakış', icon: 'LayoutDashboard' },
  { id: 'tickets', label: 'Biletlerim', icon: 'Ticket' },
  { id: 'favorites', label: 'Favorilerim', icon: 'Heart' },
  { id: 'membership', label: 'Üyelik', icon: 'Crown' },
  { id: 'listings', label: 'İlanlarım', icon: 'List' },
  { id: 'orders', label: 'Siparişler', icon: 'Package' },
  { id: 'achievements', label: 'Başarılar', icon: 'Trophy' },
  { id: 'settings', label: 'Ayarlar', icon: 'Settings' }];

  // NEW: Load user tickets from Supabase
  useEffect(() => {
    const loadUserTickets = async () => {
      try {
        const registrations = await registrationService?.getMyRegistrations();

        // Helper function to extract first image URL from images array
        const getFirstImageUrl = (images) => {
          if (!images || !Array.isArray(images) || images?.length === 0) {
            return '/no_image.png';
          }
          const firstImage = images?.[0];
          if (typeof firstImage === 'string') {
            try {
              const parsed = JSON.parse(firstImage);
              return parsed?.url || '/no_image.png';
            } catch {
              return firstImage;
            }
          }
          return firstImage?.url || '/no_image.png';
        };

        // Transform registrations to ticket format for DigitalTickets component
        const transformedTickets = registrations?.map(reg => {
          const eventImage = getFirstImageUrl(reg?.event?.images);
          
          // Map database status to display status
          let displayStatus = 'expired';
          if (reg?.status === 'approved' || reg?.status === 'confirmed') {
            displayStatus = 'active';
          } else if (reg?.status === 'pending') {
            displayStatus = 'upcoming';
          } else if (reg?.status === 'cancelled') {
            displayStatus = 'cancelled';
          } else if (reg?.status === 'rejected') {
            displayStatus = 'rejected';
          }
          
          return {
            id: reg?.id,
            eventId: reg?.eventId,
            eventName: reg?.event?.title || 'Event',
            eventImage: eventImage,
            eventImageAlt: `${reg?.event?.title || 'Etkinlik'} görseli`,
            date: reg?.event?.eventDate ? new Date(reg?.event?.eventDate)?.toLocaleDateString('tr-TR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }) : 'Tarih belirtilmemiş',
            location: reg?.event?.location || 'Konum belirtilmemiş',
            ticketType: reg?.equipmentRental ? 'Ekipman Kiralama Dahil' : 'Kendi Ekipmanı',
            ticketNumber: reg?.id?.slice(0, 8)?.toUpperCase(),
            status: displayStatus
          };
        });
        
        setTickets(transformedTickets);
      } catch (err) {
        console.error('Error loading tickets:', err);
      }
    };

    if (userProfile && !profileLoading) {
      loadUserTickets();
    }
  }, [userProfile, profileLoading]);

  // Load favorites function - accessible throughout component
  const loadFavorites = async () => {
    try {
      setFavoritesLoading(true);
      const data = await favoriteService?.getMyFavorites();
      setFavorites(data || []);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setFavoritesLoading(false);
    }
  };

  // UPDATED: Load favorites when favorites tab is active
  useEffect(() => {
    loadFavorites();
  }, [activeSection, userProfile, profileLoading]);

  // NEW: Load user achievements from Supabase
  useEffect(() => {
    const loadAchievements = async () => {
      if (userProfile?.id) {
        try {
          const { data, error } = await supabase
            ?.from('user_profiles')
            ?.select('achievements')
            ?.eq('id', userProfile?.id)
            ?.single();

          if (error) throw error;
          
          // Set achievements from database (already in correct format)
          setAchievements(data?.achievements || []);
        } catch (err) {
          console.error('Error loading achievements:', err);
          setAchievements([]);
        }
      }
    };

    loadAchievements();
  }, [userProfile?.id]);

  // NEW: Load user orders from Supabase
  useEffect(() => {
    const loadUserOrders = async () => {
      if (userProfile?.id) {
        try {
          const ordersData = await getUserOrders();
          
          // Transform Supabase data to match OrderHistory component format
          const transformedOrders = ordersData?.map(order => ({
            id: order?.id,
            orderNumber: order?.id?.slice(0, 8)?.toUpperCase(),
            date: new Date(order?.created_at)?.toLocaleDateString('tr-TR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }),
            total: `₺${parseFloat(order?.total_amount)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`,
            totalAmount: parseFloat(order?.total_amount) || 0,
            itemCount: order?.order_items?.length || 0,
            status: order?.status,
            payment_status: order?.payment_status || null,
            payment_method: order?.payment_method || null,
            cancel_reason: order?.cancel_reason || null,
            tracking_number: order?.tracking_number || null,
            tracking_company: order?.tracking_company || null,
            trackingNumber: order?.tracking_number || null,
            items: (order?.order_items || [])?.map(item => {
              // Try to find the product object in singular or plural key
              const productData = item?.products || item?.product || {};
              
              return {
                id: item?.id,
                name: productData?.title || 'Ürün Adı Yok',
                image: productData?.image_urls?.[0]?.url || productData?.images?.[0] || '/no_image.png',
                imageAlt: `${productData?.title || 'Ürün'} görseli`,
                quantity: item?.quantity,
                price: `₺${parseFloat(item?.price_at_purchase)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
              };
            })
          })) || [];
          
          setOrders(transformedOrders);
        } catch (err) {
          console.error('Error loading user orders:', err);
          setOrders([]);
        }
      }
    };

    if (activeSection === 'orders' || activeSection === 'overview') {
      loadUserOrders();
    }
  }, [userProfile?.id, activeSection]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!profileLoading) {
      setLoading(false);
    }
  }, [profileLoading]);

  const handleEditProfile = () => {
    setShowEditModal(true);
  };

  const handleSaveProfile = async (formData) => {
    try {
      const updates = {
        full_name: formData?.name,
        avatar_url: formData?.avatar,
        bio: formData?.bio,
        experience_level: formData?.experienceLevel,
        phone: formData?.phone,
        // TC number and emergency contact fields
        tc_number: formData?.tcNumber,
        birth_date: formData?.birthDate,
        emergency_contact_name: formData?.emergencyContactName,
        emergency_contact_phone: formData?.emergencyContactPhone,
        // New shipping address fields
        city: formData?.city,
        district: formData?.district,
        address_line: formData?.addressLine,
        zip_code: formData?.zipCode,
        // New social media & equipment fields
        instagram_handle: formData?.instagramHandle,
        website_url: formData?.websiteUrl,
        equipment_details: formData?.equipmentDetails,
        // SUP Information fields
        weight_kg: formData?.weightKg,
        height_cm: formData?.heightCm,
        // SUP profile fields from registration
        has_equipment: formData?.hasEquipment,
        location: formData?.location
      };
      
      const { error } = await updateProfile(updates);
      if (error) throw error;
      
      setShowEditModal(false);
    } catch (err) {
      console.error('Profile update error:', err);
    }
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  const handleConfirmUpgrade = async () => {
    try {
      const result = await membershipService?.upgradeToPro(userProfile?.id);
      
      if (result?.success) {
        // Update local user profile
        await updateProfile({ membership_tier: 'pro' });
        
        // Show success message
        alert('Tebrikler! PRO üyeliğiniz başarıyla aktif edildi.');
        
        // Close modal
        setShowUpgradeModal(false);
        
        // Reload page to reflect changes
        window.location?.reload();
      } else {
        alert('Üyelik yükseltme sırasında bir hata oluştu: ' + result?.error);
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      alert('Üyelik yükseltme sırasında bir hata oluştu.');
    }
  };

  const handleManageBilling = () => {
    navigate('/billing-management');
  };

  const getTotalFavoritesCount = () => {
    return favorites?.length || 0;
  };

  // Helper function to get status label and color
  const getOrderStatus = (status) => {
    if (status === 'delivered') {
      return { label: 'Teslim Edildi', color: 'text-green-600 bg-green-50 border-green-200' };
    } else if (status === 'shipped' || status === 'in_progress') {
      return { label: 'Kargoda', color: 'text-blue-600 bg-blue-50 border-blue-200' };
    } else if (status === 'cancelled') {
      return { label: 'İptal Edildi', color: 'text-red-700 bg-red-100 border-red-300' };
    } else {
      // Default for pending, processing, or any other status
      return { label: 'Sipariş Alındı', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' };
    }
  };

  const handleToggleFavorite = async () => {
    // Refresh favorites after toggling
    try {
      const data = await favoriteService?.getMyFavorites();
      setFavorites(data || []);
    } catch (err) {
      console.error('Error refreshing favorites:', err);
    }
  };

  const renderFavoritesSection = () => {
    if (favoritesLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      );
    }

    // Group favorites by type
    const groupedFavorites = {
      event: favorites?.filter(f => f?.itemType === 'event') || [],
      spot: favorites?.filter(f => f?.itemType === 'spot') || [],
      product: favorites?.filter(f => f?.itemType === 'product') || [],
      marketplace: favorites?.filter(f => f?.itemType === 'marketplace') || []
    };

    const hasAnyFavorites = favorites?.length > 0;

    if (!hasAnyFavorites) {
      return (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Heart" className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz favori yok</h3>
          <p className="text-gray-500 mb-6">Beğendiğiniz içerikleri favorilerinize ekleyin</p>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {/* Events Section */}
        {groupedFavorites?.event?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Calendar" className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-900">Etkinlikler</h3>
              <span className="text-sm text-gray-500">({groupedFavorites?.event?.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groupedFavorites?.event?.map(event => (
                <EventCard key={event?.id} event={event} />
              ))}
            </div>
          </div>
        )}

        {/* Spots Section */}
        {groupedFavorites?.spot?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="MapPin" className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-900">Spotlar</h3>
              <span className="text-sm text-gray-500">({groupedFavorites?.spot?.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groupedFavorites?.spot?.map(spot => (
                <SpotCard key={spot?.id} spot={spot} />
              ))}
            </div>
          </div>
        )}

        {/* Store Products Section */}
        {groupedFavorites?.product?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ShoppingBag" className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-900">Mağaza Ürünleri</h3>
              <span className="text-sm text-gray-500">({groupedFavorites?.product?.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groupedFavorites?.product?.map(product => (
                <ProductCard 
                  key={product?.id} 
                  product={product}
                  onAddToCart={() => {}}
                  onQuickView={() => {}}
                  onToggleWishlist={() => loadFavorites()}
                  isInWishlist={true}
                />
              ))}
            </div>
          </div>
        )}

        {/* Marketplace Items Section */}
        {groupedFavorites?.marketplace?.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Package" className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-900">Kullanıcı Pazarı</h3>
              <span className="text-sm text-gray-500">({groupedFavorites?.marketplace?.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groupedFavorites?.marketplace?.map(item => (
                <ListingCard 
                  key={item?.id} 
                  listing={item}
                  onContact={() => {}}
                  onWishlist={() => loadFavorites()}
                  isWishlisted={true}
                  isOwner={false}
                  onMarkAsSold={() => {}}
                  onDelete={() => {}}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading || profileLoading) {
    return (
      <>
        <Header />
        <div className="main-content bg-background min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Profil yükleniyor...</p>
          </div>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Header />
        <div className="main-content bg-background min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-muted-foreground">Profil bilgisi bulunamadı. Lütfen giriş yapın.</p>
          </div>
        </div>
      </>
    );
  }

  const membership = {
    isPro: user?.isPro,
    nextBillingDate: "15 Ocak 2026",
    paymentMethod: "Kredi Kartı (****1234)"
  };

  return (
    <>
      <Header />
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <ProfileHeader user={user} onEditProfile={handleEditProfile} />
          
          <EmailVerificationBanner />

          {/* Abandoned Payment Recovery Banner */}
          {(() => {
            const pendingCardOrder = orders?.find(o => o?.payment_status === 'pending' && o?.payment_method === 'credit_card');
            if (!pendingCardOrder) return null;
            return (
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6 shadow-sm rounded-r-lg flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="text-orange-500 text-2xl">⏳</span>
                  <div>
                    <h4 className="text-orange-800 font-bold">Yarım Kalan Ödemeniz Var!</h4>
                    <p className="text-orange-700 text-sm mt-0.5">Siparişinizin iptal olmaması için lütfen ödeme işlemini tamamlayınız.</p>
                  </div>
                </div>
                <button
                  onClick={() => setActiveSection('orders')}
                  className="ml-4 text-orange-700 underline text-sm font-semibold hover:text-orange-900 whitespace-nowrap"
                >
                  Siparişlere Git →
                </button>
              </div>
            );
          })()}

          <div className="flex flex-col lg:flex-row gap-4 md:gap-6 mb-6">
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-card rounded-xl shadow-organic p-3 md:p-4 sticky top-20">
                <nav className="space-y-1">
                  {sections?.map((section) =>
                  <button
                    key={section?.id}
                    onClick={() => setActiveSection(section?.id)}
                    className={`w-full flex items-center gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg text-sm md:text-base font-medium transition-smooth ${
                    activeSection === section?.id ?
                    'bg-primary text-primary-foreground' :
                    'text-foreground hover:bg-muted'}`
                    }>

                      <Icon name={section?.icon} size={18} />
                      <span>{section?.label}</span>
                    </button>
                  )}
                </nav>
              </div>
            </div>

            <div className="flex-1 space-y-4 md:space-y-6">
              {activeSection === 'overview' && (
                <>
                  {/* Profile Information Display - Read Only */}
                  <div className="bg-card rounded-xl shadow-organic p-6 space-y-6">
                    <h3 className="text-xl font-bold text-foreground mb-4">Profil Bilgileri</h3>
                    
                    {/* Group 1: Kişisel Bilgiler */}
                    <div className="space-y-4">
                      <h4 className="text-xl font-bold text-foreground flex items-center gap-2 pb-2 border-b-2 border-primary/20">
                        <Icon name="User" size={22} />
                        Kişisel Bilgiler
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                            Ad Soyad
                            {isFieldMissing(user?.name) && (
                              <Icon name="AlertCircle" size={16} className="text-red-500" title="Bu alan eksik" />
                            )}
                          </label>
                          <div className={`px-3 py-2 bg-muted/50 border rounded-lg text-foreground ${
                            isFieldMissing(user?.name) ? 'border-red-300 bg-red-50/50' : 'border-border'
                          }`}>
                            {user?.name || '-'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">Kullanıcı Adı</label>
                          <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                            {user?.username || '-'}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                          Biyografi
                          {isFieldMissing(user?.bio) && (
                            <Icon name="AlertCircle" size={16} className="text-red-500" title="Bu alan eksik" />
                          )}
                        </label>
                        <div className={`px-3 py-2 bg-muted/50 border rounded-lg text-foreground min-h-[60px] ${
                          isFieldMissing(user?.bio) ? 'border-red-300 bg-red-50/50' : 'border-border'
                        }`}>
                          {user?.bio || '-'}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">E-posta</label>
                          <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                            {user?.email || '-'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                            Telefon
                            {isFieldMissing(user?.phone) && (
                              <Icon name="AlertCircle" size={16} className="text-red-500" title="Bu alan eksik" />
                            )}
                          </label>
                          <div className={`px-3 py-2 bg-muted/50 border rounded-lg text-foreground ${
                            isFieldMissing(user?.phone) ? 'border-red-300 bg-red-50/50' : 'border-border'
                          }`}>
                            {user?.phone || '-'}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                            TC Numarası
                            {isFieldMissing(user?.tcNumber, 'tc') && (
                              <Icon name="AlertCircle" size={16} className="text-red-500" title="Bu alan eksik" />
                            )}
                          </label>
                          <div className={`px-3 py-2 bg-muted/50 border rounded-lg text-foreground ${
                            isFieldMissing(user?.tcNumber, 'tc') ? 'border-red-300 bg-red-50/50' : 'border-border'
                          }`}>
                            {user?.tcNumber || '-'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                            Doğum Tarihi
                            {isFieldMissing(user?.birthDate) && (
                              <Icon name="AlertCircle" size={16} className="text-red-500" title="Bu alan eksik" />
                            )}
                          </label>
                          <div className={`px-3 py-2 bg-muted/50 border rounded-lg text-foreground ${
                            isFieldMissing(user?.birthDate) ? 'border-red-300 bg-red-50/50' : 'border-border'
                          }`}>
                            {user?.birthDate ? new Date(user?.birthDate)?.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Group 2: SUP Bilgileri */}
                    <div className="space-y-4 pt-6 border-t border-border">
                      <h4 className="text-xl font-bold text-foreground flex items-center gap-2 pb-2 border-b-2 border-primary/20">
                        <Icon name="Waves" size={22} />
                        SUP Bilgileri
                      </h4>
                      <p className="text-sm text-muted-foreground">Ekipman seçimi için gerekli bilgiler</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">Deneyim Seviyesi</label>
                          <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                            {user?.experienceLevel === 'beginner' ? 'Başlangıç - Yeni başlıyorum' :
                             user?.experienceLevel === 'intermediate' ? 'Orta - Deneyimliyim' :
                             user?.experienceLevel === 'advanced'? 'İleri - Profesyonelim' : user?.experienceLevel || '-'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">Ekipman Durumu</label>
                          <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                            {user?.hasEquipment === 'yes' ? 'Evet, kendi ekipmanım var' :
                             user?.hasEquipment === 'no' ? 'Hayır, kiralama ihtiyacım var' :
                             user?.hasEquipment === 'planning'? 'Almayı planlıyorum' : user?.hasEquipment || '-'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">Konum</label>
                          <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                            {user?.location === 'istanbul' ? 'İstanbul' :
                             user?.location === 'izmir' ? 'İzmir' :
                             user?.location === 'antalya' ? 'Antalya' :
                             user?.location === 'bodrum' ? 'Bodrum' :
                             user?.location === 'marmaris' ? 'Marmaris' :
                             user?.location === 'fethiye' ? 'Fethiye' :
                             user?.location === 'cesme' ? 'Çeşme' :
                             user?.location === 'alacati' ? 'Alaçatı' :
                             user?.location === 'other'? 'Diğer' : user?.location || '-'}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Kilo (kg)</label>
                        <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                          {user?.weightKg || '-'}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Boy (cm)</label>
                        <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                          {user?.heightCm || '-'}
                        </div>
                      </div>
                    </div>

                    {/* Group 3: Acil Durum İletişim */}
                    <div className="space-y-4 pt-6 border-t border-border">
                      <h4 className="text-xl font-bold text-red-600 flex items-center gap-2 pb-2 border-b-2 border-red-200">
                        <Icon name="Phone" size={22} />
                        Acil Durum İletişim
                      </h4>
                      <p className="text-sm text-muted-foreground">Etkinliklerde acil durumlarda ulaşılacak kişi bilgileri</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">Acil Durum Kişisi</label>
                          <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                            {user?.emergencyContactName || '-'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">Acil Durum Telefonu</label>
                          <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                            {user?.emergencyContactPhone || '-'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Group 4: Teslimat Adresi */}
                    <div className="space-y-4 pt-6 border-t border-border">
                      <h4 className="text-xl font-bold text-foreground flex items-center gap-2 pb-2 border-b-2 border-primary/20">
                        <Icon name="MapPin" size={22} />
                        Teslimat Adresi
                      </h4>
                      <p className="text-sm text-muted-foreground">Mağazadan sipariş vermek için gereklidir</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">İl</label>
                          <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                            {user?.city || '-'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-muted-foreground mb-1">İlçe</label>
                          <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                            {user?.district || '-'}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Açık Adres</label>
                        <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground min-h-[80px]">
                          {user?.addressLine || '-'}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Posta Kodu</label>
                        <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                          {user?.zipCode || '-'}
                        </div>
                      </div>
                    </div>

                    {/* Group 5: Sosyal Medya & Ekipman */}
                    <div className="space-y-4 pt-6 border-t border-border">
                      <h4 className="text-lg font-semibold text-foreground flex items-center gap-2">
                        <Icon name="Share2" size={20} />
                        Sosyal Medya & Ekipman
                      </h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Instagram</label>
                        <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                          {user?.instagramHandle || '-'}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Web Sitesi</label>
                        <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground">
                          {user?.websiteUrl || '-'}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-muted-foreground mb-1">Ekipman Detayı</label>
                        <div className="px-3 py-2 bg-muted/50 border border-border rounded-lg text-foreground min-h-[80px]">
                          {user?.equipmentDetails || '-'}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Kullandığınız SUP board, kürek ve diğer ekipmanlar
                        </p>
                      </div>
                    </div>
                  </div>

                  {activities?.length > 0 ? (
                    <ActivityTimeline activities={activities} />
                  ) : null}
                </>
              )}

              {activeSection === 'tickets' && (
                tickets?.length > 0 ? (
                  <DigitalTickets tickets={tickets} />
                ) : (
                  <div className="bg-card rounded-xl shadow-organic p-8 text-center">
                    <p className="text-muted-foreground">Henüz bilet yok</p>
                  </div>
                )
              )}

              {activeSection === 'favorites' && renderFavoritesSection()}

              {activeSection === 'membership' && (
                <MembershipPlan
                  membership={membership}
                  onUpgrade={handleUpgrade}
                  onManageBilling={handleManageBilling} />
              )}

              {activeSection === 'listings' && (
                <UserListings />
              )}

              {activeSection === 'orders' && (
                <div className="bg-card rounded-xl shadow-organic p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Icon name="Package" size={28} className="text-primary fill-primary" />
                    <div>
                      <h2 className="text-2xl font-bold text-foreground">Mağaza Siparişleri</h2>
                      <p className="text-muted-foreground text-sm">
                        Mağazadan yaptığınız siparişler ve kargo takibi
                      </p>
                    </div>
                  </div>

                  {/* Store Orders - Always Displayed */}
                  <div>
                    {orders?.length > 0 ? (
                      <div className="space-y-4">
                        {orders?.map(order => {
                          // Calculate progress percentage based on status
                          let progressPercent = 0;
                          if (order?.status === 'pending') progressPercent = 33;
                          else if (order?.status === 'processing') progressPercent = 33;
                          else if (order?.status === 'shipped') progressPercent = 66;
                          else if (order?.status === 'delivered') progressPercent = 100;

                          const statusInfo = getOrderStatus(order?.status);

                          return (
                            <div 
                              key={order?.id} 
                              className="border border-border rounded-xl bg-card p-4 cursor-pointer hover:shadow-lg transition-shadow duration-200"
                              onClick={() => setSelectedOrder(order)}
                            >
                              <div className="flex justify-between border-b border-border pb-2 mb-4">
                                <span className="font-bold text-foreground">#{order?.orderNumber}</span>
                                <span className="text-primary font-semibold">{order?.total}</span>
                              </div>
                              {/* Status Badge */}
                              <div className="mb-3">
                                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${statusInfo?.color}`}>
                                  {statusInfo?.label}
                                </span>
                              </div>
                              {/* Progress Bar Visualization */}
                              <div className="mb-4">
                                <div className="flex justify-between text-xs text-muted-foreground mb-2">
                                  <span>Sipariş</span>
                                  <span>Kargoda</span>
                                  <span>Teslim</span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-primary transition-all duration-500" 
                                    style={{ width: `${progressPercent}%` }}
                                  />
                                </div>
                              </div>
                              {/* Items List */}
                              <div className="space-y-2">
                                {order?.items?.map(item => (
                                  <div key={item?.id} className="flex gap-3">
                                    <img 
                                      src={item?.image} 
                                      alt={item?.imageAlt || item?.name}
                                      className="w-12 h-12 rounded object-cover" 
                                    />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-foreground">{item?.name}</p>
                                      <p className="text-xs text-muted-foreground">{item?.quantity} adet</p>
                                    </div>
                                    <p className="text-sm font-semibold text-foreground">{item?.price}</p>
                                  </div>
                                ))}
                              </div>
                              {/* Cancellation Alert in Order List */}
                              {order?.status === 'cancelled' && (
                                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex flex-col gap-1 w-full">
                                  <span className="font-semibold text-red-700">⚠️ Siparişiniz İptal Edildi</span>
                                  {order?.cancel_reason ? (
                                    <span className="text-sm text-red-600"><strong>Açıklama:</strong> {order?.cancel_reason}</span>
                                  ) : (
                                    <span className="text-sm text-red-600">Siparişiniz iptal edilmiştir. Detaylı bilgi için bizimle iletişime geçebilirsiniz.</span>
                                  )}
                                </div>
                              )}
                              {/* Resume Payment CTA for pending credit card orders */}
                              {order?.payment_status === 'pending' && order?.payment_method === 'credit_card' && (
                                <div className="mt-3 pt-3 border-t border-orange-200">
                                  <button
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-colors animate-pulse"
                                    onClick={async (e) => {
                                      e?.stopPropagation();
                                      try {
                                        const { data, error } = await (await import('../../lib/supabase'))?.supabase?.functions?.invoke('process-custom-payment', {
                                          body: {
                                            existingOrderId: order?.id,
                                            totalAmount: order?.totalAmount,
                                            shippingFee: 0,
                                            formData: {
                                              email: userProfile?.email || '',
                                              fullName: userProfile?.full_name || '',
                                              phone: userProfile?.phone || '',
                                              address: userProfile?.address_line || '',
                                              city: userProfile?.city || '',
                                              district: userProfile?.district || '',
                                              postalCode: userProfile?.zip_code || '',
                                              tc: userProfile?.tc_number || '',
                                            },
                                            userId: userProfile?.id,
                                            clientOrigin: window.location?.origin,
                                          }
                                        });
                                        if (error) throw new Error(error.message);
                                        if (!data?.success) throw new Error(data?.error || 'Ödeme başlatılamadı.');
                                        if (data?.paymentUrl) {
                                          window.location.href = data?.paymentUrl;
                                        } else if (data?.htmlContent) {
                                          const w = window.open();
                                          w?.document?.write(data?.htmlContent);
                                          w?.document?.close();
                                        } else {
                                          throw new Error("Paynkolay'dan geçerli bir ödeme bağlantısı alınamadı.");
                                        }
                                      } catch (err) {
                                        alert('Ödeme başlatılamadı: ' + (err?.message || err));
                                      }
                                    }}
                                  >
                                    💳 Ödemeyi Tamamla
                                  </button>
                                </div>
                              )}
                              {order?.trackingNumber && (
                                <div className="mt-4 pt-4 border-t border-border">
                                  <p className="text-xs text-muted-foreground">
                                    Takip No: <span className="font-mono font-medium text-foreground">{order?.trackingNumber}</span>
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Icon name="ShoppingBag" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                          Henüz mağaza siparişi yok
                        </h3>
                        <p className="text-muted-foreground mb-6">
                          Mağazadan ürün satın aldığınızda siparişleriniz burada görünecek.
                        </p>
                        <button
                          onClick={() => navigate('/official-store')}
                          className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
                        >
                          Mağazaya Git
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeSection === 'achievements' && (
                <div className="bg-card rounded-xl shadow-organic p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Icon name="Trophy" size={24} className="text-primary" />
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-foreground">Başarılar</h2>
                      <p className="text-sm text-muted-foreground">Kazandığınız rozetler ve başarılar</p>
                    </div>
                  </div>
                  <AchievementShowcase achievements={achievements} userId={userProfile?.id} />
                </div>
              )}

              {activeSection === 'settings' && (
                <SettingsTab user={user} />
              )}
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <EditProfileModal
          user={user}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveProfile} />
      )}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onConfirm={handleConfirmUpgrade}
      />
      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="Package" size={24} className="text-primary fill-primary" />
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Sipariş Detayı</h2>
                  <p className="text-sm text-gray-500">#{selectedOrder?.orderNumber}</p>
                </div>
              </div>
              <div>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getOrderStatus(selectedOrder?.status)?.color}`}>
                  {getOrderStatus(selectedOrder?.status)?.label}
                </span>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Product List */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Icon name="ShoppingBag" size={20} />
                  Ürünler
                </h3>
                {selectedOrder?.items && selectedOrder?.items?.length > 0 ? (
                  <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 mb-6">
                    {selectedOrder?.items?.map((item, index) => (
                      <div key={index} className="flex items-start gap-4 border-b border-border pb-4 last:border-0 last:pb-0">
                        {/* Image */}
                        <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 border border-border">
                          <img src={item?.image} alt={item?.name} className="w-full h-full object-cover" />
                        </div>
                        {/* Info */}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground line-clamp-2">{item?.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">Adet: {item?.quantity}</p>
                        </div>
                        {/* Price */}
                        <div className="text-right">
                          <p className="text-sm font-semibold text-primary">{item?.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-sm text-gray-500">Ürün bilgisi bulunamadı</p>
                  </div>
                )}
              </div>

              {/* Delivery Info */}
              {(user?.city || user?.district) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Icon name="MapPin" size={20} />
                    Teslimat Bilgileri
                  </h3>
                  <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                    {user?.city && (
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Şehir:</span> {user?.city}
                      </p>
                    )}
                    {user?.district && (
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">İlçe:</span> {user?.district}
                      </p>
                    )}
                    {user?.addressLine && (
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Adres:</span> {user?.addressLine}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Order Summary */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Icon name="Receipt" size={20} />
                  Sipariş Özeti
                </h3>
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Ara Toplam</span>
                    <span className="font-medium text-gray-900">{selectedOrder?.total}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Kargo Ücreti</span>
                    <span className="font-medium text-gray-900">₺0,00</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 flex justify-between">
                    <span className="font-semibold text-gray-900">Toplam</span>
                    <span className="font-bold text-primary text-lg">{selectedOrder?.total}</span>
                  </div>
                </div>
              </div>

              {/* Tracking Number */}
              {selectedOrder?.tracking_number && (
                <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-col gap-2 w-full">
                  <div className="flex items-center gap-2 border-b border-emerald-100 pb-2">
                    <Icon name="Package" size={18} className="text-emerald-600" />
                    <span className="font-semibold text-emerald-800">Kargo Takip Bilgileri</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div className="flex flex-col">
                      <span className="text-xs text-emerald-600 uppercase font-medium">Kargo Firması</span>
                      <span className="text-sm font-semibold text-emerald-900">{selectedOrder?.tracking_company || '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-emerald-600 uppercase font-medium">Takip Numarası</span>
                      <span className="text-sm font-bold text-emerald-900 select-all tracking-wider">{selectedOrder?.tracking_number}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Cancellation Alert */}
              {selectedOrder?.status === 'cancelled' && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="AlertTriangle" size={20} className="text-red-600 flex-shrink-0" />
                    <span className="font-semibold text-red-800">Siparişiniz İptal Edildi.</span>
                  </div>
                  {selectedOrder?.cancel_reason && (
                    <p className="text-sm text-red-700 ml-7">
                      <span className="font-medium">İptal Nedeni:</span> {selectedOrder?.cancel_reason}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
              <button
                onClick={() => setSelectedOrder(null)}
                className="w-full px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

 };

export default UserProfile;